// Dates of recurring events for the countdown pages. Everything is computed from the
// year, so the pages never need a yearly edit. Dates are local calendar days (midnight
// in the viewer's own time zone), which is what a countdown to "Halloween" means.

export type FixedEvent =
  | 'new-year'
  | 'halloween'
  | 'thanksgiving'
  | 'black-friday'
  | 'valentines-day'
  | 'easter'
  | 'boxing-day'
  | 'christmas';

export const EVENT_NAMES: Record<FixedEvent, string> = {
  'new-year': "New Year's Day",
  halloween: 'Halloween',
  thanksgiving: 'Thanksgiving (US)',
  'black-friday': 'Black Friday',
  'valentines-day': "Valentine's Day",
  easter: 'Easter Sunday',
  'boxing-day': 'Boxing Day',
  christmas: 'Christmas Day',
};

/** Western (Gregorian) Easter Sunday, anonymous Gregorian algorithm. */
export function easterSunday(year: number): { month: number; day: number } {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { month: month - 1, day };
}

/** US Thanksgiving: the fourth Thursday of November. */
export function thanksgivingDay(year: number): number {
  const firstWeekday = new Date(year, 10, 1).getDay();
  const firstThursday = 1 + ((4 - firstWeekday + 7) % 7);
  return firstThursday + 21;
}

/** Month (0-based) and day of an event in a given year. */
export function eventDay(event: FixedEvent, year: number): { month: number; day: number } {
  switch (event) {
    case 'new-year':
      return { month: 0, day: 1 };
    case 'halloween':
      return { month: 9, day: 31 };
    case 'thanksgiving':
      return { month: 10, day: thanksgivingDay(year) };
    case 'black-friday':
      return { month: 10, day: thanksgivingDay(year) + 1 };
    case 'valentines-day':
      return { month: 1, day: 14 };
    case 'easter':
      return easterSunday(year);
    case 'boxing-day':
      return { month: 11, day: 26 };
    case 'christmas':
      return { month: 11, day: 25 };
  }
}

/** The next start of the event on or after `now`, at local midnight. */
export function nextEvent(event: FixedEvent, now: Date): Date {
  for (const year of [now.getFullYear(), now.getFullYear() + 1]) {
    const { month, day } = eventDay(event, year);
    const at = new Date(year, month, day);
    // Still counting "today" as the day itself until it is over.
    if (at.getTime() + 86_400_000 > now.getTime()) return at;
  }
  const { month, day } = eventDay(event, now.getFullYear() + 2);
  return new Date(now.getFullYear() + 2, month, day);
}

/** Next occurrence of a yearly month/day (birthdays, anniversaries), local midnight. */
export function nextYearly(month: number, day: number, now: Date): Date {
  for (const year of [now.getFullYear(), now.getFullYear() + 1]) {
    // Feb 29 birthdays fall back to Feb 28 in common years.
    const leap = new Date(year, 1, 29).getMonth() === 1;
    const d = month === 1 && day === 29 && !leap ? 28 : day;
    const at = new Date(year, month, d);
    if (at.getTime() + 86_400_000 > now.getTime()) return at;
  }
  return new Date(now.getFullYear() + 1, month, day);
}

export type Remaining = {
  total: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

export function remaining(target: Date, now: Date): Remaining {
  const total = Math.max(0, target.getTime() - now.getTime());
  const s = Math.floor(total / 1000);
  return {
    total,
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
  };
}
